import React, { useEffect, useMemo, useRef } from 'react'
// import { View, Image as PDFImage } from '@react-pdf/renderer'
import { compiler } from 'markdown-to-jsx'
import { OrderTypes } from './MarkdownList'
import {
	CollapseText,
	ImageWrapper,
	ViewWrapper,
	List,
	ListItem,
	HeaderComponent,
} from './helpers'
import {
	imagetoCanvas,
	canvastoFile,
	EImageType,
	urltoImage,
} from 'image-conversion'
import { MarkdownContext } from './types'
import { theme } from '@mission.io/styles'
import { PdfContextProps } from '../types'
import './mathJaxWindowType'

const MathJaxFontScale = 2 / 3

/**
 * Wrapper - wraps all of the markdown
 *
 * @param {Object} props - the react props object
 * @param {children:JSX.Element} {props.children} - the markdown elements
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element} - an element which wraps the markdown elements
 */
function Wrapper({
	children,
	markdownContext,
}: {
	children: JSX.Element
	markdownContext: MarkdownContext
}): JSX.Element {
	const { View } = markdownContext.ReactPdf
	return (
		<View>
			<CollapseText markdownContext={markdownContext}>{children}</CollapseText>
		</View>
	)
}

/**
 * MathLanguage - display the MathJax (latex like) math language
 *
 * @param {Object} props - the react props
 * @param {string} props.children - the code string
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element}
 */
export function MathLanguage({
	children: _children,
	markdownContext,
}: {
	children: string | null
	markdownContext: MarkdownContext
}): JSX.Element | null {
	const { Image } = markdownContext.ReactPdf

	const children = typeof _children === 'string' ? _children : ''

	const currentImageUrl = useRef<string | null>(null)
	const currentUpdateVersion = useRef<number>(0)
	const currentColor =
		typeof markdownContext.styleOverrides.color === 'string'
			? markdownContext.styleOverrides.color
			: 'currentColor'
	const [dimensions, getUrlPromise] = useMemo(() => {
		currentUpdateVersion.current += 1
		const updateVersion = currentUpdateVersion.current
		if (!children || typeof children !== 'string') {
			return [{ width: 0, height: 0 }, null]
		}
		try {
			if (!window.MathJax?.tex2svg) {
				throw new Error(
					'Mathjax is either not loaded or did not load the tex2svg function'
				)
			}
			const svg = window.MathJax.tex2svg(children).getElementsByTagName(
				'svg'
			)[0]
			// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			svg
				.querySelectorAll('[stroke="currentColor"]')
				.forEach(element => element.setAttribute('stroke', currentColor))
			// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			svg
				.querySelectorAll('[fill="currentColor"]')
				.forEach(element => element.setAttribute('fill', currentColor))

			// const svgString = svg.outerHTML.replaceAll('currentColor', 'green')
			const encodedUrl =
				'data:image/svg+xml;base64,' +
				// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				window.btoa(unescape(encodeURIComponent(svg.outerHTML)))
			return [
				{
					width:
						// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						svg.width.baseVal.valueInSpecifiedUnits *
						// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						svg.width.baseVal.unitType *
						MathJaxFontScale,
					height:
						// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						svg.height.baseVal.valueInSpecifiedUnits *
						// @ts-expect-error TS18048 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
						svg.height.baseVal.unitType *
						MathJaxFontScale,
				},
				() =>
					urltoImage(encodedUrl)
						.then(img => imagetoCanvas(img))
						.then(canvas => canvastoFile(canvas, undefined, EImageType.PNG))
						.then(blob => {
							// only update the image if we are still supposed to use the image after the transformations are finished
							if (updateVersion === currentUpdateVersion.current) {
								const previousImageUrl = currentImageUrl.current
								if (previousImageUrl) {
									URL.revokeObjectURL(previousImageUrl)
								}
								currentImageUrl.current = URL.createObjectURL(blob)
								return currentImageUrl.current
							}
							return ''
						}),
			]
		} catch (error) {
			console.error(error)
			return [{ width: 0, height: 0 }, null]
		}
	}, [children, currentColor])
	useEffect(() => {
		// clear unused memory by removing the url after the component unloads and add a mark that we should discard any images still being created by this component
		return () => {
			const currentUrl = currentImageUrl.current
			if (currentUrl) {
				URL.revokeObjectURL(currentUrl)
			}
			currentUpdateVersion.current = Infinity
		}
	}, [])

	if (!children || typeof children !== 'string') {
		return null
	}
	return (
		<>
			{getUrlPromise ? (
				<Image
					src={getUrlPromise}
					style={{
						width: `${dimensions.width}pt`,
						height: `${dimensions.height}pt`,
					}}
				/>
			) : (
				<CollapseText markdownContext={markdownContext}>
					{children}
				</CollapseText>
			)}
		</>
	)
}

/**
 * NoLanguageCodeBlock - the component which is used when a code block does not specify a language
 *
 * @param {Object} props - the react props
 * @param {string} props.children - the code string
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element}
 */
function NoLanguageCodeBlock({
	children,
	markdownContext,
}: {
	children: string | null
	markdownContext: MarkdownContext
}): JSX.Element {
	return (
		<CollapseText
			style={markdownContext.pdfStyles.codeHighlight}
			markdownContext={markdownContext}>
			{children ?? ''}
		</CollapseText>
	)
}

const LanguageRenderers: Record<
	string,
	(props: {
		children: string | null
		markdownContext: MarkdownContext
	}) => JSX.Element | null
> = {
	math: MathLanguage,
	katex: MathLanguage,
	latex: MathLanguage,
}

/**
 * Code - Parses the markdown code block into the correct language
 *
 * @param {Object} props - the react props
 * @param {string} props.children - the code string
 * @param {string} props.className - the language string from markdown-to-jsx
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element}
 */
export function Code({
	children,
	className,
	markdownContext,
}: {
	children: string
	className: string
	markdownContext: MarkdownContext
}): JSX.Element {
	const { View } = markdownContext.ReactPdf

	let languageString = className
	let codeSnippet = children
	const isBlock = className?.startsWith('lang-') || false
	if (
		!languageString &&
		typeof children === 'string' &&
		children.startsWith('lang-')
	) {
		const matches = children.match(/^lang-[^\s]*\s/)
		if (matches?.length) {
			languageString = matches[0].slice(0, matches[0].length - 1)
			codeSnippet = children.slice(languageString.length + 1, children.length)
		}
	}

	const language = languageString?.replace('lang-', '')
	const Component = LanguageRenderers[language] ?? NoLanguageCodeBlock
	if (isBlock) {
		return (
			<View>
				<Component markdownContext={markdownContext}>{codeSnippet}</Component>
			</View>
		)
	}
	return <Component markdownContext={markdownContext}>{codeSnippet}</Component>
}

/**
 * NotImplemented - a catch all component for rendering components which are not implemented, only displays the children.
 *
 * @param {{children:JSX.Element}} {children} - the children of the unimplemented component
 * @param {markdownContext} props.markdownContext - the reactPDF and styles available to be used
 *
 * @return {JSX.Element}
 */
function NotImplemented({
	children,
	markdownContext,
}: {
	children: JSX.Element
	markdownContext: MarkdownContext
}): JSX.Element {
	return (
		<CollapseText markdownContext={markdownContext}>{children}</CollapseText>
	)
}

const BASE_FONT_SIZE = 10 // pts

export type UserDefinedOverride = Record<
	string,
	React.FC<Record<string, never>>
>

/**
 * PDFMarkdown - display markdown in a pdf
 *
 * @param {Object} props - the React props
 * @param {string} props.markdown - the markdown to render
 * @param {ReactPdf} props.ReactPdf - the ReactPdf module
 * @param {?string} props.color? - the color to force the markdown to render with
 * @param {?UserDefinedOverride} props.overrides?  - a mapping of components names to the components which should be used
 */
export default function PDFMarkdown({
	markdown,
	ReactPdf: reactPdf,
	color,
	overrides: userProvidedOverrides,
}: {
	markdown: string
	color?: string | null
	overrides?: UserDefinedOverride | null | undefined
} & PdfContextProps): JSX.Element {
	const [styles, styleOverrides] = useMemo(() => {
		const styleOverrides: { color?: string } = {}
		if (color) {
			styleOverrides.color = color
		}
		const styles = reactPdf.StyleSheet.create({
			section: {
				...styleOverrides,
				margin: 10,
				padding: 10,
				flexGrow: 1,
			},
			link: {
				...styleOverrides,
				color: 'rgb(69, 88, 222)',
			},
			list: {
				...styleOverrides,
				marginLeft: '5%',
				marginBottom: `${BASE_FONT_SIZE}pt`,
			},
			listItem: {
				...styleOverrides,
				flexDirection: 'row',
			},
			italics: {
				...styleOverrides,
				fontStyle: 'italic',
			},
			bold: {
				...styleOverrides,
				fontWeight: 'bold',
			},
			paragraph: {
				...styleOverrides,
				marginBottom: `${BASE_FONT_SIZE}pt`,
			},
			imageWrapper: {
				...styleOverrides,
				maxWidth: '25vw',
				maxHeight: '25vh',
			},
			strikeThrough: {
				textDecoration: 'line-through',
			},
			underline: {
				textDecoration: 'underline',
			},
			image: { ...styleOverrides, objectFit: 'contain' },
			codeHighlight: { ...styleOverrides, color: theme.primary },
			h1: {
				...styleOverrides,
				fontWeight: 'bold',
				fontSize: '36px',
			},
			h2: {
				...styleOverrides,
				fontWeight: 'bold',
				fontSize: '24px',
			},
			h3: {
				...styleOverrides,
				fontWeight: 'bold',
				fontSize: '20px',
			},
			h4: {
				...styleOverrides,
				fontWeight: 'bold',
				fontSize: '16px',
			},
			h5: {
				...styleOverrides,
				fontSize: '16px',
			},
			h6: {
				...styleOverrides,
				fontSize: '14px',
			},
		})
		return [styles, styleOverrides]
	}, [reactPdf, color])

	const [overrides, markdownContext] = useMemo(() => {
		const markdownContext: MarkdownContext = {
			ReactPdf: reactPdf,
			pdfStyles: styles,
			styleOverrides: styleOverrides,
		}
		// tells markdown-to-jsx how to replace components
		const overrides: {
			[tag: string]: {
				component: React.FC<never> | React.FC<Record<string, never>>
				props?: unknown
			}
		} = {
			p: {
				component: ViewWrapper,
				props: {
					style: styles.paragraph,
					markdownContext,
				},
			},
			span: {
				component: CollapseText,
				props: {
					markdownContext,
				},
			},
			em: {
				// italics
				component: CollapseText,
				props: {
					style: styles.italics,
					markdownContext,
				},
			},
			strong: {
				// bold
				component: CollapseText,
				props: {
					style: styles.bold,
					markdownContext,
				},
			},
			link: {
				component: CollapseText,
				props: { markdownContext },
			},
			img: {
				component: ImageWrapper,
				props: {
					markdownContext,
				},
			},
			ul: {
				component: List,
				props: {
					type: OrderTypes.UNORDERED,
					markdownContext,
				},
			},
			ol: {
				component: List,
				props: {
					type: OrderTypes.ORDERED,
					markdownContext,
				},
			},
			li: {
				component: ListItem,
				props: { markdownContext },
			},
			code: {
				component: Code,
				props: {
					markdownContext,
				},
			},
			pre: {
				component: CollapseText,
				props: { markdownContext },
			},
			del: {
				component: CollapseText,
				props: {
					style: styles.strikeThrough,
					markdownContext,
				},
			},
			u: {
				component: CollapseText,
				props: {
					style: styles.underline,
					markdownContext,
				},
			},
			div: {
				component: (reactPdf.View as never) as React.FC<never>,
			},
			h1: {
				component: HeaderComponent,
				props: {
					style: styles.h1,
					markdownContext,
				},
			},
			h2: {
				component: HeaderComponent,
				props: {
					style: styles.h2,
					markdownContext,
				},
			},
			h3: {
				component: HeaderComponent,
				props: {
					style: styles.h3,
					markdownContext,
				},
			},
			h4: {
				component: HeaderComponent,
				props: {
					style: styles.h4,
					markdownContext,
				},
			},
			h5: {
				component: HeaderComponent,
				props: {
					style: styles.h5,
					markdownContext,
				},
			},
			h6: {
				component: HeaderComponent,
				props: {
					style: styles.h6,
					markdownContext,
				},
			},
		}

		if (userProvidedOverrides) {
			Object.keys(userProvidedOverrides).forEach(override => {
				overrides[override] = {
					// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
					component: userProvidedOverrides[override],
					props: {
						markdownContext,
					},
				}
			})
		}

		// an object used to determine which components are allowed to render in markdown
		return [
			new Proxy(
				{},
				{
					get: function(_, name) {
						return (
							overrides[name as string] || {
								component: NotImplemented,
								props: { name, markdownContext },
							}
						)
					},
				}
			),
			markdownContext,
		]
	}, [reactPdf, styles, styleOverrides, userProvidedOverrides])

	const WrapperComponent = useMemo(
		() =>
			function WrapperComponent({ children }: { children: JSX.Element }) {
				return <Wrapper markdownContext={markdownContext}>{children}</Wrapper>
			},
		[markdownContext]
	)
	return useMemo(
		() =>
			compiler(markdown, {
				overrides,
				wrapper: WrapperComponent,
			}),
		[markdown, overrides, WrapperComponent]
	)
}
